import { error } from 'react-notification-system-redux';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';
import { stopSubmit } from 'redux-form';
import i18next from 'i18next';

import { getAuthProps } from '@topconagriculture/auth';
import { User, UserCreateInput } from '@topconagriculture/frontend-utils';
import { hideLoader, showLoader } from '@topconagriculture/loader';
import { getUserProfile } from '@topconagriculture/profiling';

import { notificationsConfig } from '../../../config/constants';
import { LoadersNames } from '../../ui/ui.constants';
import { companyMutations, companyQueries } from '../../../core/graphql/company';
import * as actions from './company.user.actions';
import { createCompanyUser, getCompanyUserList } from './company.user.actions';
import { UserPayload } from './company.user.reducer';
import { hasApolloError } from '../../../core/graphql/gqlErrors';
import { Forms } from '../../form/form.selector';
import { farmActions } from '../../actions';
import { UserRoles } from '../../user/user.types';
import { getDateFormat, getDateTimeFormat } from '../../../core/helpers/utils/utils';
import { successHandler } from '../../../core/helpers/utils/sagaUtils';

export enum CompanyUserErrors {
  AlreadyExist = 'AlreadyExist',
  INVALID_COUNTRY = 'InvalidCountry',
}

export function* getCompanyUserListSaga() {
  const user = yield select(getUserProfile);
  if (!user.roles.includes(UserRoles.ADMIN)) {
    return;
  }
  yield put(showLoader(LoadersNames.fetchCompanyUserList));
  try {
    const { companyId } = yield select(getAuthProps);
    const result = yield call(companyQueries.getCompanyUsers, companyId);
    yield put(
      actions.getCompanyUserList.done({
        params: companyId,
        result,
      }),
    );
  } catch {
    yield put(
      error({
        translationKey: 'companyUser:profiling:errors:cant_load_users',
        ...notificationsConfig.defaultConf,
      }),
    );
  }
  yield put(hideLoader(LoadersNames.fetchCompanyUserList));
}

const toUserCreateInput = (userForm: User & UserPayload): UserCreateInput => ({
  firstName: userForm.firstName,
  lastName: userForm.lastName,
  email: userForm.email,
  countryCode: userForm.countryCode,
  locale: userForm.locale,
  state: userForm.state,
  addressLine1: userForm.addressLine1,
  addressLine2: userForm.addressLine2,
  phone: userForm.phone,
  city: userForm.city,
  zipCode: userForm.zipCode,
  roles: userForm.roles,
  settings: {
    dateFormat: getDateFormat(),
    timeFormat: getDateTimeFormat(),
    regionalSettingsName: userForm.settings.regionalSettingsName,
    preferredAppId: userForm.settings.preferredAppId,
  },
});

export function* companyUserCreateSaga(action: Action<User & UserPayload>) {
  const userForm = action.payload;
  const formId = Forms.companyUserNew;

  const payload = toUserCreateInput(userForm);
  yield put(showLoader(LoadersNames.createCompanyUser));
  try {
    const result = yield call(companyMutations.createCompanyUser, payload as UserCreateInput);
    yield put(
      createCompanyUser.done({
        params: userForm,
        result,
      }),
    );
    yield getCompanyUserListSaga();
    successHandler(action.meta, result.addUser.user);
  } catch (err) {
    const AlreadyExistError = hasApolloError(err, 409, CompanyUserErrors.AlreadyExist);

    if (
      AlreadyExistError &&
      AlreadyExistError.code === CompanyUserErrors.AlreadyExist &&
      AlreadyExistError.status === 409
    ) {
      yield put(stopSubmit(formId, { email: i18next.t('companyUser:profiling:errors:user_exists') } as any));
      yield put(
        error({
          translationKey: 'companyUser:profiling:errors:user_exists',
          ...notificationsConfig.defaultConf,
        }),
      );
    } else if (err.code === CompanyUserErrors.INVALID_COUNTRY) {
      yield put(stopSubmit(formId, { email: i18next.t('companyUser:profiling:errors:invalid_country') } as any));
      yield put(
        error({
          translationKey: 'companyUser:profiling:errors:invalid_country',
          ...notificationsConfig.defaultConf,
        }),
      );
    } else {
      yield put(
        error({
          translationKey: 'companyUser:profiling:errors:cant_create_user',
          ...notificationsConfig.defaultConf,
        }),
      );
    }
  }
  yield put(hideLoader(LoadersNames.createCompanyUser));
}

export function* watchCompanyUserSaga() {
  yield all([
    yield takeLatest(createCompanyUser.started, companyUserCreateSaga),
    yield takeLatest(getCompanyUserList.started, getCompanyUserListSaga),
    yield takeLatest(farmActions.getFarmsListRequest.done, getCompanyUserListSaga),
  ]);
}
